import type {JSONContent} from "@tiptap/core";
import type {Printout} from "crqlar-react-common/dist/models/printout/printout.model";

export type PrintoutLanguageCode = string;

export const DEFAULT_PRINTOUT_LANGUAGE = "en";

export const DEFAULT_PRINTOUT_CONTENT = JSON.stringify({
    type: "doc",
    content: [{type: "paragraph"}],
});

const ISO_LANGUAGE = /^[a-z]{2}$/;

/**
 * What the document helpers actually read: the localized content and its presentation, without the
 * stored template's identity. A `Printout` satisfies it, and so does an ad-hoc render target such as
 * the print harness, which renders a document that belongs to no template.
 */
export type PrintoutDocumentSource = Omit<Printout, "type">;

export const normalizePrintoutLanguage = (value?: string | null): PrintoutLanguageCode | undefined => {
    const normalized = value?.trim().split("-")[0]?.toLowerCase();
    return normalized && ISO_LANGUAGE.test(normalized) ? normalized : undefined;
};

export const getPrintoutLanguages = (printout?: PrintoutDocumentSource | null): PrintoutLanguageCode[] => (
    Array.from(new Set(
        Object.keys(printout?.contentByLanguage ?? {})
            .map(normalizePrintoutLanguage)
            .filter((language): language is string => !!language),
    ))
);

// Fall back to the first stored language.
export const resolvePrintoutLanguage = (
    printout?: PrintoutDocumentSource | null,
    requestedLanguage?: string | null,
): PrintoutLanguageCode | undefined => {
    const requested = normalizePrintoutLanguage(requestedLanguage);
    const languages = getPrintoutLanguages(printout);

    return requested && languages.includes(requested)
        ? requested
        : languages[0];
};

export const printoutHasLanguage = (printout: Printout, language?: string | null) => {
    const normalized = normalizePrintoutLanguage(language);
    return !!normalized && getPrintoutLanguages(printout).includes(normalized);
};

export const printoutHasLayouts = (printout?: Printout | null) => getPrintoutLanguages(printout).length > 0;

export interface ResolvedPrintoutDocument {
    content: string;
    language: PrintoutLanguageCode;
}

export const resolvePrintoutDocument = (
    printout?: PrintoutDocumentSource | null,
    requestedLanguage?: string | null,
): ResolvedPrintoutDocument | undefined => {
    const language = resolvePrintoutLanguage(printout, requestedLanguage);
    if (!language) return undefined;

    return {
        language,
        content: printout?.contentByLanguage?.[language] ?? DEFAULT_PRINTOUT_CONTENT,
    };
};

export const setPrintoutLanguages = (
    printout: Printout,
    languages: string[],
): Printout => {
    const normalizedLanguages = Array.from(new Set(
        languages
            .map(normalizePrintoutLanguage)
            .filter((language): language is string => !!language),
    ));
    const headingFontFamily = getPrintoutHeadingFontFamily(printout);
    const emptyContent = headingFontFamily
        ? withHeadingFontFamily(DEFAULT_PRINTOUT_CONTENT, headingFontFamily)
        : DEFAULT_PRINTOUT_CONTENT;

    return {
        ...printout,
        contentByLanguage: Object.fromEntries(normalizedLanguages.map(language => [
            language,
            printout.contentByLanguage?.[language] ?? emptyContent,
        ])),
    };
};

export const setPrintoutDocumentContent = (
    printout: Printout,
    language: string,
    content: string,
): Printout => {
    const normalized = normalizePrintoutLanguage(language);
    if (!normalized || !getPrintoutLanguages(printout).includes(normalized)) return printout;

    return {
        ...printout,
        contentByLanguage: {
            ...printout.contentByLanguage,
            [normalized]: content,
        },
    };
};

export const parsePrintoutContent = (content?: string): JSONContent => {
    const normalized = content?.trim();
    if (!normalized || normalized === "undefined") return JSON.parse(DEFAULT_PRINTOUT_CONTENT) as JSONContent;

    try {
        const parsed = JSON.parse(normalized);
        if (parsed && typeof parsed === "object" && parsed.type === "doc") return parsed as JSONContent;
    } catch (error) {
        console.warn("Failed to parse printout content", error);
    }

    return JSON.parse(DEFAULT_PRINTOUT_CONTENT) as JSONContent;
};

export const serializePrintoutContent = (content: JSONContent) => JSON.stringify(content);

export const getPrintoutHeadingFontFamily = (printout: Printout): string | undefined => {
    const document = resolvePrintoutDocument(printout);
    if (!document) return undefined;

    const family = parsePrintoutContent(document.content).attrs?.headingFontFamily;
    return typeof family === "string" ? family : undefined;
};

const withHeadingFontFamily = (content: string, family: string | undefined): string => {
    const document = parsePrintoutContent(content);
    if ((document.attrs?.headingFontFamily || undefined) === family) return content;

    return serializePrintoutContent({
        ...document,
        attrs: {...document.attrs, headingFontFamily: family ?? null},
    });
};

// Fonts are chosen once in settings; each language layout carries the heading font
// in its existing JSON payload, without requiring a new API property.
export const setPrintoutHeadingFontFamily = (printout: Printout, family?: string): Printout => ({
    ...printout,
    contentByLanguage: Object.fromEntries(Object.entries(printout.contentByLanguage).map(([language, content]) => [
        language,
        withHeadingFontFamily(content, family || undefined),
    ])),
});
